import React from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { useModuleConfigTheme } from '../../../shared/hooks/useModuleConfigTheme';
import FacturationFormModalTabs from '../../../pages/facturation/components/FacturationFormModalTabs';

/** Standard overlay for portaled modals (ApprobationsCrudShell pattern). */
export const QAWAEM_MODAL_OVERLAY_CLASS =
  'fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4';

export function getQawaemModalPanelClass(isLight) {
  return `backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${
    isLight
      ? 'qawaem-modal-light border'
      : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'
  }`;
}

export function getQawaemModalPanelStyle(isLight, maxWidth = '1200px') {
  return isLight
    ? {
        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
        width: '90%',
        maxWidth,
      }
    : {
        boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
        width: '90%',
        maxWidth,
      };
}

/**
 * Standard Qawaem ERP create/edit modal — matches ApprobationsCrudShell pattern.
 * Always render via: {open && <QawaemErpCrudModal open ... />}
 */
export default function QawaemErpCrudModal({
  open,
  onClose,
  title,
  icon: Icon,
  formId = 'qawaem-crud-form',
  saveLabel,
  saving = false,
  showSaveButton = false,
  onSubmit: onSubmitLegacy,
  maxWidth = '1200px',
  testId = 'qawaem-crud-modal',
  tabs,
  activeTab,
  onTabChange,
  tabsAriaLabel,
  children,
}) {
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const showSave = showSaveButton || onSubmitLegacy;

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className={QAWAEM_MODAL_OVERLAY_CLASS}
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        data-qawaem-crud-modal="true"
        data-testid={testId}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={getQawaemModalPanelClass(isLight)}
        style={getQawaemModalPanelStyle(isLight, maxWidth)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
          <h2 className={`text-2xl font-bold flex items-center gap-2 ${cfgTheme.title}`}>
            {Icon ? <Icon className="h-6 w-6 text-blue-400" /> : null}
            {title}
          </h2>
          <div className="flex items-center gap-3">
            {showSave && saveLabel ? (
              <button
                type="submit"
                form={formId}
                disabled={saving}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-50"
              >
                <Save size={18} />
                {saveLabel}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className={isLight ? 'text-slate-500 hover:text-slate-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}
            >
              <X size={24} />
            </button>
          </div>
        </div>
        {tabs?.length ? (
          <FacturationFormModalTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
            isLight={isLight}
            ariaLabel={tabsAriaLabel || title}
          />
        ) : null}
        <div className="overflow-y-auto p-6 flex-1 min-h-0">{children}</div>
      </motion.div>
    </div>,
    document.body,
  );
}

/** Standard view/read-only modal. */
export function QawaemErpViewModal({
  open,
  onClose,
  title,
  icon: Icon,
  maxWidth = '720px',
  testId = 'qawaem-view-modal',
  children,
}) {
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className={QAWAEM_MODAL_OVERLAY_CLASS}
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        data-testid={testId}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={getQawaemModalPanelClass(isLight)}
        style={getQawaemModalPanelStyle(isLight, maxWidth)}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
          <h2 className={`text-2xl font-bold flex items-center gap-2 ${cfgTheme.title}`}>
            {Icon ? <Icon className="h-6 w-6 text-blue-400" /> : null}
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={isLight ? 'text-slate-500 hover:text-slate-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}
          >
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
      </motion.div>
    </div>,
    document.body,
  );
}
